<template>
    <validation-observer ref="fields">

        <v-row class="pb-2 mx-0 pt-4">
            <v-col class="col-12 col-sm-12 col-md-6 col-lg-6 py-0 px-6 pt-3">
                <validation-provider name="Nome" rules="required" v-slot="{ errors }">
                    <v-text-field
                        type="text"
                        label="Nome"
                        v-model="model['name']"
                        :error-messages="errors"
                        outlined
                        dense
                        ref="name"
                    ></v-text-field>
                </validation-provider>
            </v-col>

            <v-col class="col-12 col-sm-12 col-md-6 col-lg-6 py-0 px-6 pt-3">
                <address-autocomplete
                    v-model="model['address']"
                    :lat.sync="model['latitude']"
                    :lng.sync="model['longitude']"
                    label="Cerca indirizzo"
                    keep-coords
                ></address-autocomplete>
            </v-col>

            <v-col class="col-12 py-0 px-6 pt-3">
                <div ref="map" class="place-map rounded"></div>
                <div class="d-flex align-center flex-wrap caption py-2">
                    <span v-if="mapError" class="error--text">Mappa non disponibile, inserisci le coordinate a mano.</span>
                    <span v-else class="grey--text">Clicca sulla mappa o trascina il cerchio per spostare la sede, trascina i punti sul bordo per cambiare il raggio.</span>
                    <v-btn
                        color="primary"
                        text
                        small
                        class="ml-auto"
                        :loading="locating"
                        @click="useCurrentPosition"
                    ><v-icon small left>mdi-crosshairs-gps</v-icon>Usa la mia posizione</v-btn>
                </div>
            </v-col>

            <v-col class="col-12 col-sm-12 col-md-4 py-0 px-6 pt-3">
                <validation-provider name="Latitudine" rules="required|double" v-slot="{ errors }">
                    <v-text-field
                        type="number"
                        label="Latitudine"
                        v-model="model['latitude']"
                        :error-messages="errors"
                        outlined
                        dense
                        ref="latitude"
                    ></v-text-field>
                </validation-provider>
            </v-col>

            <v-col class="col-12 col-sm-12 col-md-4 py-0 px-6 pt-3">
                <validation-provider name="Longitudine" rules="required|double" v-slot="{ errors }">
                    <v-text-field
                        type="number"
                        label="Longitudine"
                        v-model="model['longitude']"
                        :error-messages="errors"
                        outlined
                        dense
                        ref="longitude"
                    ></v-text-field>
                </validation-provider>
            </v-col>

            <v-col class="col-12 col-sm-12 col-md-4 py-0 px-6 pt-3">
                <validation-provider name="Raggio" rules="required" v-slot="{ errors }">
                    <v-text-field
                        type="number"
                        label="Raggio (metri)"
                        v-model="model['range']"
                        :error-messages="errors"
                        outlined
                        dense
                        ref="range"
                    ></v-text-field>
                </validation-provider>
            </v-col>

        </v-row>

    </validation-observer>
</template>

<script>
import AddressAutocomplete from '@/components/common/AddressAutocomplete'
import { importLibrary, DEFAULT_CENTER } from '@/services/GoogleMapsService'
import { GeolocationService } from '@/services/native'
import * as Sentry from '@sentry/vue'

const DEFAULT_RANGE = 100

export default {
	name: 'place-fields',
	components: {
		AddressAutocomplete
	},
	props: {
		model: Object,
		info: Object,
	},

	data() {
		return {
			map: null,
			circle: null,
			syncing: false,
			mapError: false,
			locating: false
		}
	},

	watch: {
		// SimpleCrud riusa lo stesso componente per sedi diverse
		model() {
			this.syncFromModel(true)
		},
		'model.latitude'() {
			this.syncFromModel()
		},
		'model.longitude'() {
			this.syncFromModel()
		},
		'model.range'() {
			this.syncFromModel()
		}
	},

	mounted() {
		this.initMap()
	},

	methods: {
		async initMap() {
			try {
				const { Map, Circle } = await importLibrary('maps')

				this.map = new Map(this.$refs.map, {
					center: DEFAULT_CENTER,
					zoom: 6,
					mapTypeControl: false,
					streetViewControl: false,
					clickableIcons: false
				})

				this.circle = new Circle({
					map: this.map,
					center: DEFAULT_CENTER,
					radius: DEFAULT_RANGE,
					visible: false,
					editable: true,
					draggable: true,
					fillColor: '#e75e5a',
					fillOpacity: 0.3,
					strokeColor: '#e65253',
					strokeOpacity: 1,
					strokeWeight: 2
				})

				this.circle.addListener('center_changed', this.onCircleChanged)
				this.circle.addListener('radius_changed', this.onCircleChanged)
				this.map.addListener('click', (event) => this.moveTo(event.latLng.lat(), event.latLng.lng()))

				this.syncFromModel(true)
			} catch (e) {
				Sentry.captureException(e)
				this.mapError = true
			}
		},

		modelCenter() {
			const lat = parseFloat(this.model.latitude)
			const lng = parseFloat(this.model.longitude)

			if (isNaN(lat) || isNaN(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
				return null
			}

			return { lat, lng }
		},

		// porta sulla mappa i valori dei campi (caricamento sede, indirizzo scelto, modifica a mano)
		syncFromModel(fit = false) {
			if (!this.circle) {
				return
			}

			const center = this.modelCenter()

			if (!center) {
				this.circle.setVisible(false)
				if (fit) {
					this.map.setCenter(DEFAULT_CENTER)
					this.map.setZoom(6)
				}
				return
			}

			const range = parseFloat(this.model.range)
			if (!(range > 0)) {
				// sede nuova: posizione scelta ma raggio non ancora impostato
				this.$set(this.model, 'range', DEFAULT_RANGE)
			}

			const current = this.circle.getCenter()
			const shifted = Math.abs(current.lat() - center.lat) > 1e-7 || Math.abs(current.lng() - center.lng) > 1e-7
			const moved = !this.circle.getVisible() || shifted
			const resized = range > 0 && Math.abs(this.circle.getRadius() - range) >= 1

			if (!moved && !resized && !fit) {
				return
			}

			this.syncing = true
			this.circle.setCenter(center)
			if (range > 0) {
				this.circle.setRadius(range)
			}
			this.circle.setVisible(true)
			this.syncing = false

			if (fit || moved) {
				this.map.fitBounds(this.circle.getBounds())
			}
		},

		// il cerchio è stato trascinato o ridimensionato sulla mappa
		onCircleChanged() {
			if (this.syncing || !this.circle.getVisible()) {
				return
			}

			const center = this.circle.getCenter()
			this.$set(this.model, 'latitude', this.round(center.lat()))
			this.$set(this.model, 'longitude', this.round(center.lng()))
			this.$set(this.model, 'range', Math.round(this.circle.getRadius()))
		},

		moveTo(lat, lng) {
			this.$set(this.model, 'latitude', this.round(lat))
			this.$set(this.model, 'longitude', this.round(lng))
		},

		async useCurrentPosition() {
			this.locating = true
			try {
				const position = await GeolocationService.getCurrentPosition({ enableHighAccuracy: true })
				this.moveTo(position.coords.latitude, position.coords.longitude)
			} catch (e) {
				this.$store.commit('SET_SNACKBAR', {
					timeout: 6000,
					text: 'Impossibile ottenere la posizione',
					value: true,
					color: 'warning'
				})
			} finally {
				this.locating = false
			}
		},

		// 7 decimali = precisione al centimetro, entra nelle colonne decimal del DB
		round(value) {
			return Math.round(value * 1e7) / 1e7
		}
	},

}
</script>

<style scoped>
.place-map {
    height: 350px;
    width: 100%;
}
</style>
